import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { connect } from 'react-redux';
import { Row, Col, Container } from 'react-bootstrap';
import { getPageLocale } from '../../../../src/web/store/modules/page';
import { getPageLocale as getPageBuilderLocale } from '../../../../src/web/store/modules/pageBuilder';
import FacilityLink from '../FacilityLink';
import UnitSize from '../UnitSize';
import style from './UnitCategories.scss';

const propTypes = {
  facility: PropTypes.shape({
    pagePaths: PropTypes.arrayOf(PropTypes.shape({
      locale: PropTypes.string,
    })).isRequired,
    settings: PropTypes.shape({
      showUnavailableUnits: PropTypes.bool.isRequired,
    }).isRequired,
    unitSummary: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      availableUnitsCount: PropTypes.number,
      minPrice: PropTypes.number,
      siteUnitCategoryId: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  isEditable: PropTypes.bool,
  theme: PropTypes.string,
  unitComponent: PropTypes.node,
};

const defaultProps = {
  theme: 'primary',
  unitComponent: UnitSize,
  isEditable: undefined,
};

const UnitCategories = ({
  facility,
  isEditable,
  locale,
  theme,
  unitComponent,
}) => {
  const renderUnit = (name, minPrice, siteUnitCategoryId) => React.createElement(
    unitComponent,
    { name, price: minPrice || 'Prices', facility, siteUnitCategoryId },
  );

  const renderCategory = ({ id, name, minPrice, siteUnitCategoryId }) => {
    const { unitSummary } = facility;
    const index = unitSummary.findIndex(summary => summary.id === id);

    return (
      <Col xs={6} sm={3} md={3} key={id} className={style[theme]}>
        <FacilityLink
          facility={facility}
          className={style.clickable}
          queryString={`?unit=${index + 1}#unitstable`}
          isEditable={isEditable}
        >
          {renderUnit(name, minPrice, siteUnitCategoryId)}
        </FacilityLink>
      </Col>
    );
  };

  const hasLiveFacilityPage = (pagePaths) => {
    const pathByLocale = pagePaths.find(path => path.locale === locale);
    if (!pathByLocale) {
      return false;
    }
    return true;
  };

  // when we build a single-facility site, we add this widget to the home page,
  // but we do not publish the facility page
  // since the facility page has to be live for this widget to show data,
  // we show a helpful message in the builder if the facility page is not live
  if (!facility.pagePaths || !hasLiveFacilityPage(facility.pagePaths)) {
    if (isEditable) {
      return <div>Publish the page for the selected facility to enable this widget</div>;
    }
    return false;
  }

  // Adjust the unit summary if we need to
  let categories;
  if (facility.settings.showUnavailableUnits) {
    categories = facility.unitSummary;
  } else {
    categories = facility.unitSummary.filter(({ availableUnitsCount }) => availableUnitsCount > 0);
  }

  return (
    <Container fluid>
      <Row>
        {categories.slice(0, 3).map(renderCategory)}
      </Row>
    </Container>
  );
};

UnitCategories.propTypes = propTypes;
UnitCategories.defaultProps = defaultProps;

UnitCategories.fragments = {
  facility: gql`
    fragment UnitCategoriesFacility on Facility {
      pagePaths {
        id
        locale
      }
      settings {
        showUnavailableUnits
      }
      unitSummary {
        id
        name
        availableUnitsCount
        minPrice
        siteUnitCategoryId
      }
      ...FacilityLinkFacility
    }

    ${FacilityLink.fragments.facility}
  `,
};

export default connect(state => ({
  locale: getPageLocale(state) || getPageBuilderLocale(state),
}))(UnitCategories);
